<template>
  <div id="register">
    <campaign-banner />
    <v-container class="mt-5">
      <v-list-item-group v-if="errors.length" class="mb-4">
        <v-alert dense text type="error">
          <p v-for="(error, i) in errors" :key="i" class="mb-0">
            {{ error }}
          </p>
        </v-alert>
      </v-list-item-group>
      <v-row wrap>
        <v-flex class="pa-2" xs12 md6>
          <div class="headline mb-4">Account Information</div>
          <v-card outlined class="mb-4">
            <v-card-text>
              <v-form novalidate @submit.prevent="submit">
                <div class="grey-text mt-4">
                  <v-text-field
                    v-model="account.displayName"
                    label="Full Name"
                    prepend-icon="person"
                    outlined
                    required
                  ></v-text-field>
                  <v-text-field
                    v-model="account.email"
                    label="Email"
                    prepend-icon="mail"
                    type="email"
                    outlined
                    required
                  ></v-text-field>
                  <v-text-field
                    v-model="account.password"
                    label="Password"
                    prepend-icon="lock"
                    type="password"
                    outlined
                    required
                    invalidFeedback="Please provide a password"
                  ></v-text-field>
                  <v-text-field
                    v-model="account.confirmPassword"
                    label="Confirm Password"
                    prepend-icon="lock"
                    type="password"
                    outlined
                    required
                    invalidFeedback="Passwords do not match"
                  ></v-text-field>
                </div>
              </v-form>
              <div class="text-center">
                <v-divider class="my-4"></v-divider>
                <p class="mb-0">
                  <span>Already have an account? </span>
                  <router-link to="login">Log In</router-link>
                </p>
              </div>
            </v-card-text>
          </v-card>
        </v-flex>
        <v-flex class="pa-2" xs12 md6>
          <div class="headline mb-4">Order Summary</div>
          <v-card class="mb-4" outlined>
            <v-card-text>
              <v-simple-table>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Name</th>
                      <th class="text-right">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>COPQ Calculator</td>
                      <td v-if="subscriptionPrice > 0" class="text-right">$ {{ subscriptionPrice }}</td>
                      <td v-else class="text-right green--text">FREE</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
          </v-card>
          <div class="mb-2">
            <ul style="list-style-type:none;">
              <li>
                <v-icon small color="green lighten-1">mdi-check</v-icon>
                <span class="ml-2">Calculate your costs of poor quality on any device.</span>
              </li>
              <li>
                <v-icon small color="green lighten-1">mdi-check</v-icon>
                <span class="ml-2">Read results with specially crafted charts and graphics.</span>
              </li>
              <li>
                <v-icon small color="green lighten-1">mdi-check</v-icon>
                <span class="ml-2">Save or print your calculations.</span>
              </li>
            </ul>
          </div>
          <v-divider class="my-4"></v-divider>
          <div class="caption pb-2">Please note: Our 14-day money back guarantee applies to all purchases. Discounts and introductory prices only apply to the first term. You can cancel your plan at any time by logging into your COPQ Calculator account.</div>
        </v-flex>
      </v-row>
      <v-row wrap v-if="subscriptionPrice > 0">
        <v-flex class="pa-2" xs12 md6>
          <v-row class="px-2">
            <div class="headline mb-4">Method of Payment</div>
            <v-spacer></v-spacer>
            <p class="d-flex align-center">Authorized by<v-icon class="ml-1">fab fa-cc-stripe</v-icon></p>
          </v-row>
          <v-card outlined class="mb-2">
            <v-card-text>
              <div id="card-test" class="pa-3 pt-4">
                <!-- Stripe card form -->
              </div>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-row>
      <v-row>
        <v-btn
          class="my-4"
          color="success"
          depressed
          block
          x-large
          :disabled="!registrationEnabled"
          @click="submit"
        >
          <i class="fas fa-spinner fa-pulse" v-if="isLoading"></i>
          <span v-if="!isLoading">Complete Registration</span>
        </v-btn>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { get, every } from 'lodash';

import CampaignBanner from '@/components/CampaignBanner.vue';

import validate from '@/modules/validation-module';

export default {
  name: 'Register',
  components: {
    CampaignBanner,
  },
  data() {
    return {
      account: {},
      card: null,
      errors: [],
      isLoading: false,
    };
  },
  computed: {
    activeCampaign() {
      return this.$store.getters.activeCampaign;
    },
    subscriptionPrice() {
      return get(this.activeCampaign, 'price', 14.95);
    },
    registrationEnabled() {
      const userRequiredKeys = ['displayName', 'email', 'password', 'confirmPassword'];
      const formIsValid = every(userRequiredKeys, key => this.account[key]);
      return formIsValid && !this.isLoading;
    },
    testingStore() {
      return this.$store.getters.activeCampaign;
    },
    stripe() {
      // eslint-disable-next-line
      return Stripe('pk_live_n7YCjzsNOyYDdR1xBJm4RGgb00S3PXC4Zy');
    },
  },
  mounted(){
    if (this.subscriptionPrice > 0) {
      const elements = this.stripe.elements();
      this.card = elements.create('card');
      this.card.mount('#card-test');
    }
  },
  methods: {
    submit() {
      this.errors = [];
      this.validateUserDetails();
      if (!this.errors.length && this.subscriptionPrice > 0) {
        this.submitStripePayment();
      } else {
        this.createAccount();
      }
    },
    submitStripePayment() {
      this.isLoading = true;
      this.stripe.createToken(this.card)
        .then((result) => {
          if (result.error) {
            this.errors.push(result.error.message);
          } else {
            const stripeObject = {
              amount: this.subscriptionPrice,
              source: result.token,
            };
            this.commitStripeData(stripeObject);
          }
        })
        .catch((error) => {
          this.errors.push(error.message);
        })
    },
    commitStripeData(stripeObject) {
      const chargesRef = this.$firebase.firestore().collection('charges');
      const pushId = chargesRef.doc().id;
      this.$firebase.firestore().collection('charges').doc(pushId).set(stripeObject);
      chargesRef.doc(pushId).onSnapshot((snapShot) => {
        const charge = snapShot.data();
      
        if (charge.error) {
          this.errors.push(charge.error);
          chargesRef.doc(pushId).delete();
          return;
        }
        if (charge.status && charge.status === 'succeeded') {
          const paymentId = charge.balance_transaction;
          this.createAccount(paymentId);
        }
      })
    },
    createAccount(paymentId) {
      this.$firebase.auth().createUserWithEmailAndPassword(this.account.email, this.account.password)
        .then((created) => {
          created.user.updateProfile({
            displayName: this.account.displayName,
            paymentId,
          });
          created.user.sendEmailVerification();
          this.$firebase.auth().signInWithEmailAndPassword(this.account.email, this.account.password)
            .then((response) => {
              const user = response.user;
              this.$store.dispatch('fetchUser', { user })
              this.$router.push({
                name: 'home',
              });
            })
            .catch(err => this.errors.push(err.message));
        }).catch((error) => {
          this.errors.push(error.message);
        }).finally(() => { this.isLoading = false; });
    },
    validateUserDetails() {
      if (!validate.email(this.account.email)) {
        this.errors.push('Please enter a valid email address.');
      }
      if (!validate.password(this.account.password)) {
        this.errors.push('Passwords must contain at least 4 characters, a capitalized letter, and a number.');
      }
      if (this.account.password !== this.account.confirmPassword) {
        this.errors.push('Passwords do not match');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#card-test {
  border: thin solid rgba(0, 0, 0, 0.23);
  border-radius: 4px;
  min-height: 56px;
}
</style> 